import { IconTooltip } from '@/components'
import type { GenerationRuleCalculation } from '@/modules/companies/CompanySettings/components/SalarySettings/generation-rules/generation-rule-labels'
import { useGenerationRuleLabels } from '@/modules/companies/CompanySettings/components/SalarySettings/generation-rules/generation-rule-labels'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format } from 'date-fns'
import type { GenerationTraceModifier } from './generation-trace'
import { parseGenerationTrace } from './generation-trace'
import { formatPartOfDay } from './part-of-day'

const MOMENT = 'dd.MM HH:mm'

/** How an automatically generated diet or salary day was reached. */
export function GenerationTraceTooltip({ trace }: { trace: unknown }) {
  const translations = useTranslate({
    title: 'salary.generation-trace.title',
    rule: ['salary.generation-trace.rule', { rule: '', setting: '' }],
    fixed: ['salary.generation-trace.fixed', { amount: '' }],
    blocks: [
      'salary.generation-trace.blocks',
      { amount: '', hours: '', rounding: '', blocks: '' },
    ],
    roundedDown: 'salary.generation-trace.rounded-down',
    roundedUp: 'salary.generation-trace.rounded-up',
    chain: [
      'salary.generation-trace.chain',
      { start: '', end: '', blocks: '', value: '' },
    ],
    owned: [
      'salary.generation-trace.owned',
      { start: '', end: '', blocks: '', value: '' },
    ],
    ownedRest: [
      'salary.generation-trace.owned-rest',
      { start: '', end: '', blocks: '', value: '' },
    ],
    covered: 'salary.generation-trace.covered',
    rest: ['salary.generation-trace.rest', { start: '', end: '' }],
    modifier: ['salary.generation-trace.modifier', { name: '', effects: '' }],
    deny: 'salary.generation-trace.effect-deny',
    award: 'salary.generation-trace.effect-award',
    amountPer: [
      'salary.generation-trace.effect-amount-per',
      { amount: '', hours: '', rounding: '' },
    ],
    amountIgnored: 'salary.generation-trace.effect-amount-ignored',
    max: ['salary.generation-trace.effect-max', { max: '' }],
    salaryCode: 'salary.generation-trace.effect-salary-code',
    chainBreak: ['salary.generation-trace.chain-break', { name: '' }],
    chainContinue: ['salary.generation-trace.chain-continue', { name: '' }],
    result: ['salary.generation-trace.result', { value: '' }],
  })
  const labels = useGenerationRuleLabels()
  const parsed = parseGenerationTrace(trace)
  if (!parsed) return null

  const ruleLabel =
    labels.rule[parsed.rule as GenerationRuleCalculation] ?? parsed.rule
  const lines = [
    translations.rule({ rule: ruleLabel, setting: parsed.salaryWorkTimeName }),
  ]
  const { perWorkPeriod, chain, rest, modifiers } = parsed
  const rounding = (value: 'floor' | 'ceil' | null) =>
    value === 'floor' ? translations.roundedDown : translations.roundedUp
  if (perWorkPeriod) {
    const amount = formatPartOfDay(perWorkPeriod.amount)
    lines.push(
      perWorkPeriod.perHours
        ? translations.blocks({
            amount,
            hours: String(perWorkPeriod.perHours),
            rounding: rounding(perWorkPeriod.rounding),
            blocks: String(perWorkPeriod.blocks ?? 0),
          })
        : translations.fixed({ amount })
    )
  }
  if (chain) {
    lines.push(
      translations.chain({
        start: format(chain.start, MOMENT),
        end: format(chain.end, MOMENT),
        blocks: String(chain.blocks),
        value: formatPartOfDay(chain.value),
      })
    )
    const owned = rest ? translations.ownedRest : translations.owned
    lines.push(
      chain.owned
        ? owned({
            start: format(chain.owned.start, MOMENT),
            end: format(chain.owned.end, MOMENT),
            blocks: String(chain.ownedBlocks),
            value: formatPartOfDay(parsed.baseValue),
          })
        : translations.covered
    )
    for (const { name, action } of chain.actions)
      lines.push(
        action === 'break'
          ? translations.chainBreak({ name })
          : translations.chainContinue({ name })
      )
  } else if (rest) {
    lines.push(
      translations.rest({
        start: format(rest.start, MOMENT),
        end: format(rest.end, MOMENT),
      })
    )
  }
  for (const modifier of modifiers) {
    const effects = modifierEffects(modifier)
    if (effects.length)
      lines.push(
        translations.modifier({
          name: modifier.name,
          effects: effects.join(', '),
        })
      )
  }
  if (modifiers.length || (rest && !chain))
    lines.push(translations.result({ value: formatPartOfDay(parsed.value) }))

  function modifierEffects(modifier: GenerationTraceModifier) {
    const effects: string[] = []
    if (modifier.award === 'deny') effects.push(translations.deny)
    if (modifier.award === 'award') effects.push(translations.award)
    if (modifier.amountIgnored) effects.push(translations.amountIgnored)
    else if (modifier.amount !== null)
      effects.push(
        modifier.perHours
          ? translations.amountPer({
              amount: formatPartOfDay(modifier.amount),
              hours: String(modifier.perHours),
              rounding: rounding(modifier.rounding),
            })
          : formatPartOfDay(modifier.amount)
      )
    if (!modifier.amountIgnored && modifier.maxAmount !== null)
      effects.push(
        translations.max({ max: formatPartOfDay(modifier.maxAmount) })
      )
    if (modifier.salaryCodeChanged) effects.push(translations.salaryCode)
    return effects
  }

  return (
    // The row opens the edit dialog on click and is inert when read-only; the
    // explanation must stay reachable either way.
    <span
      className="pointer-events-auto!"
      onClick={event => event.stopPropagation()}
    >
      <IconTooltip triggerClassName="pointer-events-auto!">
        <div className="flex max-w-80 flex-col gap-1 text-sm">
          <strong>{translations.title}</strong>
          {lines.map((line, index) => (
            <span key={index}>{line}</span>
          ))}
        </div>
      </IconTooltip>
    </span>
  )
}
